import { createHash, randomUUID } from 'node:crypto'
import {
  type FetchRateControls,
  type JavaScriptRenderingInput,
  type JavaScriptRenderingMode,
  normalizeJavaScriptRenderingMode,
} from '../../fetch/page-fetcher.js'
import type { RuleCategory, RuleId, RuleSeverity } from '../../rules.js'
import type {
  SitemapDocument,
  SitemapFetchResult,
} from '../monitoring/sitemaps.js'
import type {
  CrawlPageSnapshot,
  CrawlRequestObservation,
} from '../monitoring/types.js'
import { auditCrawlPages, auditCrawlRequests } from './audit.js'

export type CrawlMode = 'site' | 'page' | 'list' | 'sitemap'

export type CrawlFetchRateConfig = FetchRateControls

export type CrawlConfig = {
  url: string
  mode: CrawlMode
  urls: string[]
  maxPages: number
  maxDepth: number
  concurrency: number
  timeoutMs: number
  include: string[]
  exclude: string[]
  respectRobots: boolean
  useSitemap: boolean
  checkExternal: boolean
  js: JavaScriptRenderingMode
  refresh: boolean
  fetchRate: CrawlFetchRateConfig
}

export type CrawlConfigInput = Omit<Partial<CrawlConfig>, 'url' | 'js'> & {
  url: string
  js?: JavaScriptRenderingInput
  projectId?: string
  site?: string
  searchMetricsLimit?: number
  ga4PropertyId?: string
  analyticsLimit?: number
  signal?: AbortSignal
  onStatus?: CrawlStatusHandler
}

export type CrawlStatusPhase =
  | 'started'
  | 'url_queued'
  | 'url_skipped'
  | 'page_started'
  | 'page_completed'
  | 'page_failed'
  | 'page_skipped'
  | 'external_links_started'
  | 'external_links_completed'
  | 'cancelled'
  | 'completed'

export type CrawlStatusEvent = {
  type: 'crawl_status'
  phase: CrawlStatusPhase
  generatedAt: string
  url?: string
  depth?: number
  statusCode?: number
  reportId?: string
  reportStatus?: CrawlReport['status']
  reason?: string
  message?: string
  discoveredUrls: number
  queuedUrls: number
  pendingUrls: number
  inFlightUrls: number
  crawledUrls: number
  skippedUrls: number
  failedUrls: number
  observedInternalLinks: number
  maxPages: number
}

export type CrawlStatusHandler = (
  event: CrawlStatusEvent,
) => void | Promise<void>

export type CrawlIssue = {
  ruleId: RuleId
  title: string
  category: RuleCategory
  severity: RuleSeverity
  url: string
  detail?: string
  evidence?: Record<string, unknown>
  searchMetrics?: CrawlPageSnapshot['searchMetrics']
}

export type CrawlIssueGroup = {
  ruleId: RuleId
  title: string
  category: RuleCategory
  severity: RuleSeverity
  count: number
  sampleUrls: string[]
}

export type CrawlRunStats = {
  discoveredUrls: number
  queuedUrls: number
  crawledUrls: number
  skippedUrls: number
  failedUrls: number
  observedInternalLinks: number
  pageLimitReached: boolean
}

export type CrawlLinkGraph = Record<string, string[]>

export type CrawlAiBotAccess = {
  userAgent: string
  allowed: boolean | null
  declared: boolean
  coveredByWildcard: boolean
}

export type CrawlAiResourceSignal = {
  url: string
  exists: boolean
  status?: number
  contentType?: string
  validJson?: boolean
}

export type CrawlAiSignals = {
  robotsTxt?: {
    url: string
    exists: boolean
    availability:
      | 'available'
      | 'absent'
      | 'access-blocked'
      | 'rate-limited'
      | 'unreachable'
    status?: number
    error?: string
    sitemapUrls: string[]
    botAccess: CrawlAiBotAccess[]
  }
  llmsTxt?: {
    url: string
    exists: boolean
    status?: number
  }
  agentResources?: CrawlAiResourceSignal[]
}

export type CrawlSitemapDiscovery = {
  dataStatus: 'complete' | 'partial' | 'unavailable'
  urlsReturned: number
  roots: Array<{
    url: string
    source: 'robots-txt' | 'default-path'
    dataStatus: 'complete' | 'partial' | 'unavailable'
    urlsReturned: number
    sitemapsFetched: number
    lastmods: SitemapFetchResult['source']['lastmods']
    documents: SitemapDocument[]
    possiblyTruncated: boolean
    warnings: string[]
  }>
}

export type CrawlExternalLinkVerification = {
  dataStatus: 'complete' | 'partial' | 'unavailable'
  discoveredLinkOccurrences: number
  retainedUrls: number
  selectedUrls: number
  fetchedUrls: number
  failedUrls: number
  deferredUrls: number
  limit: number
  warnings: string[]
}

export type CrawlDataSourceStatus =
  | 'joined'
  | 'partial'
  | 'none'
  | 'skipped'
  | 'unavailable'

export type CrawlDataSourceWindow = {
  startDate: string
  endDate: string
  days: number
}

export type CrawlSearchDataSource = {
  status: CrawlDataSourceStatus
  window?: CrawlDataSourceWindow
  totalPages: number
  queriedPages: number
  joinedMetricPages: number
  joinedQueryPages: number
  pageLimit: number
  pageLimitReached: boolean
  metricRowsReturned?: number
  queryRowsReturned?: number
  retainedRowLimit?: number
  retainedRowLimitReached?: boolean
  warning?: string
}

export type CrawlAnalyticsDataSource = {
  status: CrawlDataSourceStatus
  window?: CrawlDataSourceWindow
  totalPages: number
  queriedPages: number
  joinedPages: number
  returnedRows?: number
  availableRows?: number
  retainedRowLimit?: number
  retainedRowLimitReached?: boolean
  warning?: string
}

export type CrawlReportDataSources = {
  searchConsole: CrawlSearchDataSource
  analytics: CrawlAnalyticsDataSource
}

export type CrawlReportSummary = {
  totalPages: number
  indexablePages: number
  nonIndexablePages: number
  statusErrors: number
  discoveredUrls: number
  queuedUrls: number
  crawledUrls: number
  skippedUrls: number
  failedUrls: number
  observedInternalLinks: number
  pageLimitReached: boolean
  attemptedRequests: number
  responseRequests: number
  failedRequests: number
  abortedRequests: number
  extractionFailures: number
  requestByStatus: Record<string, number>
  avgRequestMs?: number
  highIssues: number
  mediumIssues: number
  lowIssues: number
  avgResponseMs?: number
  byStatus: Record<string, number>
  byCategory: Record<string, number>
}

export type CrawlReport = {
  id: string
  definitionId: string
  projectId?: string
  site?: string
  ga4PropertyId?: string
  generatedAt: string
  status: 'completed' | 'partial' | 'failed'
  configHash: string
  config: CrawlConfig
  summary: CrawlReportSummary
  requestEvidenceStatus: 'available' | 'partial' | 'unavailable'
  requests: CrawlRequestObservation[]
  pages: CrawlPageSnapshot[]
  issues: CrawlIssue[]
  issueGroups: CrawlIssueGroup[]
  dataSources?: CrawlReportDataSources
  ai?: CrawlAiSignals
  sitemapDiscovery?: CrawlSitemapDiscovery
  externalLinkVerification?: CrawlExternalLinkVerification
  warnings: string[]
  caveats: string[]
}

const SENSITIVE_FIELD_NAMES = new Set([
  'authorization',
  'cookie',
  'proxy-authorization',
  'set-cookie',
  'www-authenticate',
  'x-api-key',
  'api-key',
  'access-token',
  'refresh-token',
])

const SENSITIVE_PARAM_PATTERN =
  /^(access_?token|auth|api_?key|client_?secret|code|jwt|key|password|refresh_?token|secret|session|sig|signature|token)$/i

function uniqueSorted(values: string[] = []): string[] {
  return [...new Set(values.filter(Boolean))].sort()
}

function normalizeFetchRate(input: CrawlConfigInput): CrawlFetchRateConfig {
  return {
    concurrency: input.fetchRate?.concurrency ?? input.concurrency ?? 8,
    ...(input.fetchRate?.intervalCap !== undefined
      ? { intervalCap: input.fetchRate.intervalCap }
      : {}),
    ...(input.fetchRate?.intervalMs !== undefined
      ? { intervalMs: input.fetchRate.intervalMs }
      : {}),
    ...(input.fetchRate?.backpressure
      ? { backpressure: input.fetchRate.backpressure }
      : {}),
  }
}

export function normalizeCrawlConfig(input: CrawlConfigInput): CrawlConfig {
  return {
    url: new URL(input.url).toString(),
    mode: input.mode ?? 'site',
    urls: uniqueSorted(input.urls ?? []),
    maxPages: input.maxPages ?? 500,
    maxDepth: input.maxDepth ?? 16,
    concurrency: input.concurrency ?? 8,
    timeoutMs: input.timeoutMs ?? 20_000,
    include: uniqueSorted(input.include ?? []),
    exclude: uniqueSorted(input.exclude ?? []),
    respectRobots: input.respectRobots ?? true,
    useSitemap: input.useSitemap ?? true,
    checkExternal: input.checkExternal ?? true,
    js: normalizeJavaScriptRenderingMode(input.js),
    refresh: input.refresh ?? false,
    fetchRate: normalizeFetchRate(input),
  }
}

export function crawlConfigHash(config: CrawlConfigInput): string {
  const normalized = normalizeCrawlConfig(config)
  return createHash('sha256')
    .update(JSON.stringify(normalized))
    .digest('hex')
    .slice(0, 16)
}

export function crawlDefinitionId(input: {
  config: CrawlConfigInput
  site?: string
  ga4PropertyId?: string
}): string {
  const normalized = {
    config: normalizeCrawlConfig(input.config),
    site: input.site ?? null,
    ga4PropertyId: input.ga4PropertyId ?? null,
  }
  const hash = createHash('sha256')
    .update(JSON.stringify(normalized))
    .digest('hex')
    .slice(0, 20)
  return `crawl_def_${hash}`
}

export function crawlRunId(): string {
  return `crawl_${randomUUID().replaceAll('-', '')}`
}

export function groupCrawlIssues(issues: CrawlIssue[]): CrawlIssueGroup[] {
  const groups = new Map<string, CrawlIssueGroup>()
  for (const issue of issues) {
    const existing = groups.get(issue.ruleId) ?? {
      ruleId: issue.ruleId,
      title: issue.title,
      category: issue.category,
      severity: issue.severity,
      count: 0,
      sampleUrls: [],
    }
    existing.count += 1
    if (existing.sampleUrls.length < 10) {
      existing.sampleUrls.push(issue.url)
    }
    groups.set(issue.ruleId, existing)
  }
  const severityRank: Record<RuleSeverity, number> = {
    high: 3,
    medium: 2,
    low: 1,
  }
  return [...groups.values()].sort(
    (a, b) =>
      severityRank[b.severity] - severityRank[a.severity] || b.count - a.count,
  )
}

function isSensitiveFieldName(key: string): boolean {
  const lower = key.toLowerCase()
  return (
    SENSITIVE_FIELD_NAMES.has(lower) ||
    SENSITIVE_PARAM_PATTERN.test(lower.replace(/-/g, '_'))
  )
}

function sanitizeUrlString(value: string): string {
  try {
    const url = new URL(value)
    for (const key of [...url.searchParams.keys()]) {
      if (SENSITIVE_PARAM_PATTERN.test(key)) {
        url.searchParams.set(key, '[redacted]')
      }
    }
    return url.toString()
  } catch {
    return value
  }
}

function sanitizeTenantString(value: string): string {
  return value
    .replace(/https?:\/\/[^\s"'<>`]+/g, (match) => sanitizeUrlString(match))
    .replace(
      /\b(access_?token|api_?key|auth|client_?secret|jwt|password|refresh_?token|secret|session|signature|token)(\s*[=:]\s*)[^\s,;&]+/gi,
      (_match, key: string, separator: string) =>
        `${key}${separator}[redacted]`,
    )
    .replace(/\b[A-Za-z]:\\[^\s"'<>`]+/g, '[local-path]')
    .replace(/\/(?:Users|home|tmp|var\/folders)\/[^\s"'<>`]+/g, '[local-path]')
}

function sanitizeTenantValue(value: unknown, key?: string): unknown {
  if (key && isSensitiveFieldName(key)) return '[redacted]'
  if (typeof value === 'string') return sanitizeTenantString(value)
  if (Array.isArray(value))
    return value.map((item) => sanitizeTenantValue(item))
  if (!value || typeof value !== 'object') return value
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(
      ([entryKey, entry]) => [entryKey, sanitizeTenantValue(entry, entryKey)],
    ),
  )
}

function sanitizeCrawlConfig(config: CrawlConfig): CrawlConfig {
  return sanitizeTenantValue(config) as CrawlConfig
}

function sanitizePages(pages: CrawlPageSnapshot[]): CrawlPageSnapshot[] {
  return (
    sanitizeTenantValue(pages) as Array<
      CrawlPageSnapshot & { seoScore?: unknown; geoScore?: unknown }
    >
  ).map(({ seoScore: _seoScore, geoScore: _geoScore, ...page }) => page)
}

function sanitizeIssues(issues: CrawlIssue[]): CrawlIssue[] {
  return sanitizeTenantValue(issues) as CrawlIssue[]
}

function sanitizeMessages(values: string[] = []): string[] {
  return values.map((value) => sanitizeTenantString(value))
}

function compareText(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0
}

function sortPages(pages: CrawlPageSnapshot[]): CrawlPageSnapshot[] {
  return [...pages].sort((left, right) => compareText(left.url, right.url))
}

function sortRequests(
  requests: CrawlRequestObservation[],
): CrawlRequestObservation[] {
  return [...requests].sort((left, right) => {
    const byUrl = compareText(left.requestedUrl, right.requestedUrl)
    if (byUrl) return byUrl
    const byOutcome = compareText(left.outcome, right.outcome)
    if (byOutcome) return byOutcome
    const leftDetail =
      left.outcome === 'response'
        ? `${left.status}:${left.finalUrl}`
        : left.outcome === 'skipped'
          ? left.reason
          : `${left.failureKind}:${left.error}`
    const rightDetail =
      right.outcome === 'response'
        ? `${right.status}:${right.finalUrl}`
        : right.outcome === 'skipped'
          ? right.reason
          : `${right.failureKind}:${right.error}`
    return compareText(leftDetail, rightDetail)
  })
}

export function summarizeCrawlReport(input: {
  pages: CrawlPageSnapshot[]
  requests?: CrawlRequestObservation[]
  requestEvidenceStatus?: CrawlReport['requestEvidenceStatus']
  issues: CrawlIssue[]
  stats?: Partial<CrawlRunStats>
}): CrawlReportSummary {
  const byStatus: Record<string, number> = {}
  const requestByStatus: Record<string, number> = {}
  const byCategory: Record<string, number> = {}
  let responseMs = 0
  let responseCount = 0

  for (const page of input.pages) {
    byStatus[String(page.status)] = (byStatus[String(page.status)] ?? 0) + 1
    if (typeof page.responseTimeMs === 'number') {
      responseMs += page.responseTimeMs
      responseCount += 1
    }
  }
  const requests = input.requests ?? []
  const failedRequests = requests.filter(
    (request) =>
      request.outcome === 'failure' && request.failureKind !== 'aborted',
  )
  const abortedRequests = requests.filter(
    (request) =>
      request.outcome === 'failure' && request.failureKind === 'aborted',
  )
  let requestMs = 0
  let requestMsCount = 0
  for (const request of requests) {
    const status =
      request.outcome === 'response'
        ? String(request.status)
        : request.outcome === 'skipped'
          ? request.reason
          : request.failureKind === 'aborted'
            ? 'aborted'
            : 'no-response'
    requestByStatus[status] = (requestByStatus[status] ?? 0) + 1
    if (typeof request.durationMs === 'number') {
      requestMs += request.durationMs
      requestMsCount += 1
    }
  }
  for (const issue of input.issues) {
    byCategory[issue.category] = (byCategory[issue.category] ?? 0) + 1
  }

  return {
    totalPages: input.pages.length,
    indexablePages: input.pages.filter((page) => page.indexable).length,
    nonIndexablePages: input.pages.filter((page) => !page.indexable).length,
    statusErrors: input.pages.filter(
      (page) => page.status === 0 || page.status >= 400,
    ).length,
    ...crawlRunStats(input.pages, input.stats),
    attemptedRequests:
      input.requestEvidenceStatus === 'unavailable' ? 0 : requests.length,
    responseRequests: requests.filter(
      (request) => request.outcome === 'response',
    ).length,
    failedRequests: failedRequests.length,
    abortedRequests: abortedRequests.length,
    extractionFailures: requests.filter(
      (request) =>
        request.outcome === 'response' && request.extraction === 'failed',
    ).length,
    requestByStatus,
    avgRequestMs: requestMsCount
      ? Math.round(requestMs / requestMsCount)
      : undefined,
    highIssues: input.issues.filter((issue) => issue.severity === 'high')
      .length,
    mediumIssues: input.issues.filter((issue) => issue.severity === 'medium')
      .length,
    lowIssues: input.issues.filter((issue) => issue.severity === 'low').length,
    avgResponseMs: responseCount
      ? Math.round(responseMs / responseCount)
      : undefined,
    byStatus,
    byCategory,
  }
}

function crawlRunStats(
  pages: CrawlPageSnapshot[],
  stats: Partial<CrawlRunStats> = {},
): CrawlRunStats {
  const discovered = new Set<string>()
  let observedInternalLinks = 0
  for (const page of pages) {
    discovered.add(page.url)
    for (const url of page.sampleInternalLinks ?? []) discovered.add(url)
    for (const url of page.sampleExternalLinks ?? []) discovered.add(url)
    observedInternalLinks += page.outgoingInternalCount
  }
  return {
    discoveredUrls: stats.discoveredUrls ?? discovered.size,
    queuedUrls: stats.queuedUrls ?? pages.length,
    crawledUrls: stats.crawledUrls ?? pages.length,
    skippedUrls: stats.skippedUrls ?? 0,
    failedUrls:
      stats.failedUrls ??
      pages.filter((page) => page.status === 0 || page.status >= 400).length,
    observedInternalLinks: stats.observedInternalLinks ?? observedInternalLinks,
    pageLimitReached: stats.pageLimitReached ?? false,
  }
}

function normalizeLinkUrl(value: string): string | undefined {
  try {
    const url = new URL(value)
    url.hash = ''
    return url.toString()
  } catch {
    return undefined
  }
}

function deriveInternalLinkAuthority(
  pages: CrawlPageSnapshot[],
  linkGraph?: CrawlLinkGraph,
): CrawlPageSnapshot[] {
  const hasLinkGraph = linkGraph && Object.keys(linkGraph).length > 0
  const hasStoredScores = pages.some(
    (page) =>
      page.internalInlinkCount !== undefined ||
      page.internalLinkAuthorityScore !== undefined,
  )
  if (!hasLinkGraph && hasStoredScores) {
    return pages.map((page) => ({
      ...page,
      internalInlinkCount: page.internalInlinkCount ?? 0,
      internalLinkAuthorityScore: page.internalLinkAuthorityScore ?? 0,
    }))
  }

  const pageByUrl = new Map<string, string>()
  for (const page of pages) {
    for (const value of [page.url, page.finalUrl]) {
      const normalized = normalizeLinkUrl(value)
      if (normalized) pageByUrl.set(normalized, page.url)
    }
  }

  const inlinks = new Map<string, number>()
  for (const page of pages) inlinks.set(page.url, 0)

  for (const page of pages) {
    const targets = hasLinkGraph
      ? (linkGraph?.[page.url] ?? linkGraph?.[page.finalUrl] ?? [])
      : (page.sampleInternalLinks ?? [])
    const linkedPages = new Set<string>()
    for (const target of targets) {
      const normalized = normalizeLinkUrl(target)
      const linkedPage = normalized ? pageByUrl.get(normalized) : undefined
      if (!linkedPage || linkedPage === page.url) continue
      linkedPages.add(linkedPage)
    }
    for (const linkedPage of linkedPages) {
      inlinks.set(linkedPage, (inlinks.get(linkedPage) ?? 0) + 1)
    }
  }

  const maxInlinks = Math.max(0, ...inlinks.values())
  return pages.map((page) => {
    const internalInlinkCount = inlinks.get(page.url) ?? 0
    return {
      ...page,
      internalInlinkCount,
      internalLinkAuthorityScore: maxInlinks
        ? Math.round((internalInlinkCount / maxInlinks) * 100)
        : 0,
    }
  })
}

export function createCrawlReport(input: {
  id?: string
  config: CrawlConfigInput
  pages?: CrawlPageSnapshot[]
  requests?: CrawlRequestObservation[]
  requestEvidenceStatus?: CrawlReport['requestEvidenceStatus']
  issues?: CrawlIssue[]
  linkGraph?: CrawlLinkGraph
  ai?: CrawlAiSignals
  projectId?: string
  site?: string
  ga4PropertyId?: string
  dataSources?: CrawlReportDataSources
  sitemapDiscovery?: CrawlSitemapDiscovery
  externalLinkVerification?: CrawlExternalLinkVerification
  status?: CrawlReport['status']
  warnings?: string[]
  caveats?: string[]
  stats?: Partial<CrawlRunStats>
  generatedAt?: string
}): CrawlReport {
  const config = sanitizeCrawlConfig(normalizeCrawlConfig(input.config))
  const definitionId = crawlDefinitionId({
    config,
    site: input.site,
    ga4PropertyId: input.ga4PropertyId,
  })
  const pagesWithLinks = deriveInternalLinkAuthority(
    input.pages ?? [],
    input.linkGraph,
  )
  const safePagesWithLinks = sortPages(sanitizePages(pagesWithLinks))
  const requests = sortRequests(
    sanitizeTenantValue(input.requests ?? []) as CrawlRequestObservation[],
  )
  const requestEvidenceStatus =
    input.requestEvidenceStatus ??
    (input.requests ? 'available' : 'unavailable')
  if (requestEvidenceStatus === 'unavailable' && requests.length > 0) {
    throw new Error(
      'Unavailable request evidence cannot include request observations.',
    )
  }
  const issues = input.issues ?? [
    ...auditCrawlRequests(requests),
    ...auditCrawlPages(safePagesWithLinks, { startUrl: config.url }),
  ]
  const safeIssues = sanitizeIssues(issues)
  const pages = safePagesWithLinks
  return {
    id: input.id ?? crawlRunId(),
    definitionId,
    projectId: input.projectId,
    site: input.site,
    ga4PropertyId: input.ga4PropertyId,
    generatedAt: input.generatedAt ?? new Date().toISOString(),
    status: input.status ?? 'completed',
    configHash: crawlConfigHash(config),
    config,
    summary: summarizeCrawlReport({
      pages,
      requests,
      requestEvidenceStatus,
      issues: safeIssues,
      stats: input.stats,
    }),
    requestEvidenceStatus,
    requests,
    pages,
    issues: safeIssues,
    issueGroups: groupCrawlIssues(safeIssues),
    ...(input.dataSources
      ? {
          dataSources: sanitizeTenantValue(
            input.dataSources,
          ) as CrawlReportDataSources,
        }
      : {}),
    ...(input.ai
      ? { ai: sanitizeTenantValue(input.ai) as CrawlAiSignals }
      : {}),
    ...(input.sitemapDiscovery
      ? {
          sitemapDiscovery: sanitizeTenantValue(
            input.sitemapDiscovery,
          ) as CrawlSitemapDiscovery,
        }
      : {}),
    ...(input.externalLinkVerification
      ? {
          externalLinkVerification: sanitizeTenantValue(
            input.externalLinkVerification,
          ) as CrawlExternalLinkVerification,
        }
      : {}),
    warnings: sanitizeMessages(input.warnings).sort(compareText),
    caveats: sanitizeMessages(input.caveats).sort(compareText),
  }
}

export function normalizeLoadedCrawlReport(report: CrawlReport): CrawlReport {
  const requestEvidenceStatus =
    report.requestEvidenceStatus ??
    (Object.hasOwn(report as object, 'requests') ? 'available' : 'unavailable')
  const issues = sanitizeIssues(report.issues ?? [])
  const requests = sortRequests(
    (sanitizeTenantValue(report.requests ?? []) ??
      []) as CrawlRequestObservation[],
  )
  const pages = sortPages(
    sanitizePages(deriveInternalLinkAuthority(report.pages ?? [])),
  )
  return {
    ...(sanitizeTenantValue(report) as CrawlReport),
    requestEvidenceStatus,
    requests,
    definitionId:
      report.definitionId ??
      crawlDefinitionId({
        config: report.config,
        site: report.site,
        ga4PropertyId: report.ga4PropertyId,
      }),
    summary: summarizeCrawlReport({
      pages,
      requests,
      requestEvidenceStatus,
      issues,
      stats: report.summary,
    }),
    pages,
    issues,
    issueGroups: groupCrawlIssues(issues),
    warnings: sanitizeMessages(report.warnings).sort(compareText),
    caveats: sanitizeMessages(report.caveats).sort(compareText),
  }
}
